<template>
  <div class="page-container">
    <template v-if="!loadingActiveSubscription">
      <div class="page-container-body">
        <h3 class="page-title">My Plan</h3>

        <div class="plan-badge d-inline-flex mb-8" v-if="isSubscriptionAdmin">
          <template v-if="isTrialing">You are admin of your {{ brand.name }} free trial.</template>
          <template v-else-if="hasEndedTrial">Your {{ brand.name }} free trial has ended.</template>
          <template v-else>You are admin of your {{ brand.name }} {{ selectedPlan?.name }} plan.</template>
        </div>
        <template v-else-if="!isEnterpriseUser">
          <div class="d-flex flex-column align-start ga-2 mb-8" v-if="subscriptionAccessType === 'FULL'">
            <div class="plan-badge">
              You need to contact the payment admin to make any changes to the plan.
            </div>
            <div class="plan-badge" v-if="activeSubscription?.payerUserEmail">
              <span class="font-weight-bold">Payment Admin:</span> {{ activeSubscription.payerUserEmail }}
            </div>
          </div>
        </template>

        <div class="plan-info">
          <div class="plan-info-header">
            <span class="planName">{{ brand.name }} {{ isTrialing || hasEndedTrial ? 'Free Trial' : selectedPlan?.name }}</span>
            <span class="planPrice" v-if="!isEnterpriseUser && !isChoosingTrialPlan"><template v-if="isTrialing">then {{
              selectedPlan?.name }}, </template>${{
              planPricePerUser
            }}/User/{{ selectedSubscriptionType === 'monthly' ? 'Month' : 'Year' }} (excluding tax)</span>
          </div>

          <div class="plan-features">
            <ul class="features">
              <li v-for="feature in featuresPlan?.features" :key="feature" class="featureItem">
                <v-icon size="large" color="var(--blue-3)">mds:check</v-icon>
                <span class="featureItemLabel">{{ feature }}</span>
              </li>
            </ul>

            <a
              v-if="brand.links.pricing"
              :href="brand.links.pricing"
              target="_blank"
              class="viewAllPlans"
            >
              View all plans & features on the Pricing page.
            </a>
          </div>
        </div>
      </div>
      <div class="page-container-footer">
        <template v-if="isEnterpriseUser">
          <div class="plan-badge">
            <span>
              Please contact your {{ brand.name }} Account Manager or submit a support request <a
                href="https://2gpm9s.share-eu1.hsforms.com/2Fdo_b7-hSES99T__J3YREA"
                target="_blank" class="dialog-link">here</a> to make any changes to your plan
            </span>
          </div>
        </template>
        <template v-else>
          <template v-if="isSubscriptionAdmin">
            <div class="plan-badge" v-if="subscriptionAccessType === 'FULL'">
              <v-icon>mdi-calendar</v-icon>
              <span v-if="isChoosingTrialPlan">
                Your free trial ends on {{ nextBillingDate }}. Choose your plan before then to keep going. Nothing is
                charged unless you choose a plan, and never before {{ nextBillingDate }}.
              </span>
              <span
                v-else-if="!!activeSubscription?.subscriptionDetails?.cancelAt && !activeSubscription?.scheduleSubscriptionDetails">
                Your subscription will expire on {{ nextBillingDate }}.
              </span>
              <span v-else>
                <template v-if="!!scheduledPlan">
                  Your next bill will be for the {{
                    scheduledPlan.name
                  }} plan and is scheduled for {{
                    scheduledNextBillingDate
                  }}. The payment of ${{
                    scheduledNextBillingAmount
                  }}  (excluding tax) will be charged to your card ending in **** {{
                    activeSubscription?.subscriptionDetails?.cardDetails?.lastFour
                  }}.
                </template>
                <template v-else-if="activeSubscription?.subscriptionDetails?.status === 'trialing'">
                  Your {{ selectedPlan?.name }} plan starts on {{ nextBillingDate }}, and your card ending in
                  **** {{ activeSubscription?.subscriptionDetails?.cardDetails?.lastFour }} will be charged
                  ${{ activeSubscription?.subscriptionDetails?.nextBillingAmount }} (excluding tax).
                </template>
                <template v-else>
                  Your next bill is ${{ activeSubscription?.subscriptionDetails?.nextBillingAmount }} (excluding tax)
                  on {{
                    nextBillingDate
                  }}.<template v-if="hasCardOnFile"> Your card ending in **** {{ activeSubscription?.subscriptionDetails?.cardDetails?.lastFour }} will be charged.</template>
                </template>
              </span>
            </div>
            <div class="plan-badge" v-else-if="isRecoverableState">
              <v-icon color="var(--red-1)">mdi-alert-circle-outline</v-icon>
              <span>
                Your last payment failed, so your subscription is not active and your access is read-only.
                <template v-if="pastDueNextAttempt">We'll retry on {{ pastDueNextAttempt }}. </template>
                Update your payment method or cancel the plan to stop further charges.
              </span>
            </div>
            <v-spacer v-else/>
            <div class="d-flex align-center justify-center ga-2">
              <v-btn
                variant="tonal"
                size="large"
                color="var(--red-1)"
                @click="cancelSubscriptionDialogIsOpen = true"
                :loading="loadingCancelSubscription"
                v-if="(subscriptionAccessType === 'FULL' && !isChoosingTrialPlan &&
                  (!activeSubscription?.subscriptionDetails?.cancelAt ||
                    (!!activeSubscription?.subscriptionDetails?.cancelAt && activeSubscription.scheduleSubscriptionDetails))) ||
                  isRecoverableState"
              >Cancel Plan
              </v-btn>
              <v-btn
                variant="flat"
                size="large"
                :loading="loadingRenew"
                v-if="subscriptionAccessType === 'LIMITED' && !isChoosingTrialPlan && (recoveryButton.action === 'billing' || hasPaymentMethodOnFile)"
                @click="onRecovery"
              >{{ recoveryButton.label }}
              </v-btn>
              <v-btn
                v-if="isChoosingTrialPlan"
                variant="flat"
                size="large"
                @click="$router.push('/plans')"
              >
                Choose your plan
              </v-btn>
              <v-btn
                v-else-if="isReactivateState"
                variant="flat"
                size="large"
                :loading="loadingReactivate"
                @click="onReactivate"
              >
                Reactivate
              </v-btn>
              <v-btn
                v-else-if="!isRecoverableState"
                variant="flat"
                size="large"
                @click="$refs.changePlanDialog.openDialog()"
              >
                Change Plan
              </v-btn>
            </div>
          </template>
          <template v-else>
            <div class="plan-badge"
                 v-if="subscriptionAccessType === 'FULL' &&
                   !!activeSubscription?.subscriptionDetails?.cancelAt &&
                   !activeSubscription?.scheduleSubscriptionDetails">
              <v-icon>mdi-calendar</v-icon>
              <span>
                Your subscription will expire on {{ nextBillingDate }}.
              </span>
            </div>
            <v-spacer v-else/>
            <v-btn
              v-if="!!activeSubscription?.payerUserEmail && activeSubscription?.subscriptionDetails?.status !== 'trialing'"
              variant="flat"
              size="large"
              @click="startOwnSubscriptionDialogIsOpen = true"
            >
              Start my own subscription
            </v-btn>
          </template>
        </template>
      </div>
    </template>
    <template v-else>
      <div class="page-loading-container-body">
        <v-progress-circular
          indeterminate
          color="white"
          size="32"
          width="2">
        </v-progress-circular>
        <span>Loading subscription...</span>
      </div>
    </template>

    <subscription-canceled-dialog ref="subscriptionCanceledDialog"></subscription-canceled-dialog>
    <change-plan-dialog ref="changePlanDialog"></change-plan-dialog>
    <info-message-dialog
      v-if="infoMessageDialogData"
      :is-open="infoMessageDialogData.isOpen"
      :title="infoMessageDialogData.title"
      :message="infoMessageDialogData.message"
      @close="infoMessageDialogData = null"
    />

    <cancel-subscription-dialog
      :is-open="cancelSubscriptionDialogIsOpen"
      @close="cancelSubscriptionDialogIsOpen = false"
      @onCancel="onCancelSubscriptionDialogClose"
      @onChangeSubscription="cancelSubscriptionDialogIsOpen = false;$refs.changePlanDialog.openDialog()"
    />

    <start-own-subscription-dialog
      :is-open="startOwnSubscriptionDialogIsOpen"
      :payer-email="activeSubscription?.payerUserEmail"
      :current-plan-value="selectedPlan?.value"
      @close="startOwnSubscriptionDialogIsOpen = false"
      @confirm="startOwnSubscription"
    />
  </div>
</template>

<script>
import store from '@/store'
import format from 'date-fns/format'
import { mapState } from 'vuex'
import ChangePlanDialog from '@/components/ChangePlanDialog.vue'
import InfoMessageDialog from '@/components/InfoMessageDialog.vue'
import CancelSubscriptionDialog from '@/components/CancelSubscriptionDialog.vue'
import SubscriptionCanceledDialog from '@/components/SubscriptionCanceledDialog.vue'
import StartOwnSubscriptionDialog from '@/components/StartOwnSubscriptionDialog.vue'
import {
  ENTERPRISE_PLAN,
  PLANS,
  SUBSCRIPTION_STATE,
  getPlanByPriceId,
  getSubscriptionTypeByPriceId,
  getScheduledQuantity,
  hasCardOnFile,
  hasUnchosenTrial,
  hasEndedUnchosenTrial,
  isRecoverableState,
  getCurrentPricePerUser,
  getPlanPrice,
  getEntitledPlan
} from '@/utils/payments'
import brand from '@/composables/useBrand'

export default {
  components: {
    ChangePlanDialog,
    InfoMessageDialog,
    CancelSubscriptionDialog,
    SubscriptionCanceledDialog,
    StartOwnSubscriptionDialog
  },
  computed: {
    brand: () => brand,
    ...mapState({
      currentUser: state => state.user.user,
      activeSubscription: state => state.payments.activeSubscription,
      paymentMethod: state => state.payments.paymentMethod,
      subscriptionUsers: state => state.payments.subscriptionUsers,
      subscriptionAccessType: state => state.payments.subscriptionAccessType,
      subscriptionState: state => state.payments.subscriptionState,
      loadingActiveSubscription: state => state.payments.loadingActiveSubscription,
      loadingCancelSubscription: state => state.payments.loadingCancelSubscription,
      isEnterpriseUser: state => state.payments.isEnterpriseUser,
      isSubscriptionAdmin: state => state.payments.isSubscriptionAdmin
    }),
    hasCardOnFile: function () {
      return hasCardOnFile(this.activeSubscription)
    },
    isChoosingTrialPlan: function () {
      return hasUnchosenTrial(this.activeSubscription) || this.hasEndedTrial
    },
    hasEndedTrial: function () {
      return hasEndedUnchosenTrial(this.activeSubscription)
    },
    isTrialing: function () {
      return this.activeSubscription?.subscriptionDetails?.status === 'trialing'
    },
    featuresPlan: function () {
      if (this.hasEndedTrial) {
        return PLANS.find((plan) => plan.value === 'pro')
      }

      return this.isTrialing ? getEntitledPlan(this.activeSubscription) : this.selectedPlan
    },
    hasPaymentMethodOnFile: function () {
      return !!this.paymentMethod?.card?.last4 || this.hasCardOnFile
    },
    isRecoverableState: function () {
      return isRecoverableState(this.subscriptionState)
    },
    pastDueNextAttempt: function () {
      const nextAttempt = this.activeSubscription?.lastSubscriptionDetails?.latestInvoice?.nextPaymentAttempt
      if (nextAttempt) {
        return format(new Date(nextAttempt), 'MMMM dd, yyyy')
      }

      return ''
    },
    recoveryButton: function () {
      const hasClientSecret = !!this.activeSubscription?.lastSubscriptionDetails?.clientSecret
      if (this.subscriptionState === SUBSCRIPTION_STATE.PAST_DUE && hasClientSecret) {
        return { label: 'Authenticate Payment', action: 'renew' }
      }
      if (this.isRecoverableState) {
        return { label: 'Update payment method', action: 'billing' }
      }

      return { label: 'Renew Plan', action: 'renew' }
    },
    isReactivateState: function () {
      return !!this.activeSubscription?.subscriptionDetails?.cancelAt &&
        !this.activeSubscription?.scheduleSubscriptionDetails
    },
    selectedPlan: function () {
      if (this.activeSubscription) {
        if (this.isEnterpriseUser) {
          return ENTERPRISE_PLAN
        }
        const priceId = this.activeSubscription?.subscriptionDetails?.price?.id || this.activeSubscription?.lastSubscriptionDetails?.price?.id
        return getPlanByPriceId(priceId)
      }
      return null
    },
    subscriptionType: function () {
      if (this.activeSubscription) {
        const priceId = this.activeSubscription?.subscriptionDetails?.price?.id || this.activeSubscription?.lastSubscriptionDetails?.price?.id
        return getSubscriptionTypeByPriceId(priceId)
      }
      return null
    },
    nextBillingDate: function () {
      const nextBillingDate = this.activeSubscription?.subscriptionDetails?.nextBillingDate
      if (nextBillingDate) {
        return format(new Date(nextBillingDate), 'MMMM dd, yyyy')
      }
      return ''
    },
    scheduledNextBillingDate: function () {
      const startDate = this.activeSubscription?.scheduleSubscriptionDetails?.phases?.[0]?.startDate
      if (startDate) {
        return format(new Date(startDate), 'MMMM dd, yyyy')
      }
      return ''
    },
    scheduledPlan: function () {
      const scheduledPriceId = this.activeSubscription?.scheduleSubscriptionDetails?.phases?.[0]?.items?.[0]?.priceId
      if (scheduledPriceId) {
        return getPlanByPriceId(scheduledPriceId)
      }
      return null
    },
    scheduledSubscriptionType: function () {
      const scheduledPriceId = this.activeSubscription?.scheduleSubscriptionDetails?.phases?.[0]?.items?.[0]?.priceId
      if (scheduledPriceId) {
        return getSubscriptionTypeByPriceId(scheduledPriceId)
      }
      return null
    },
    scheduledNextBillingAmount: function () {
      const scheduleQuantity = getScheduledQuantity(this.activeSubscription)
      if (this.scheduledPlan) {
        const subscriptionType = this.scheduledSubscriptionType
        return scheduleQuantity * (subscriptionType === 'monthly' ? this.scheduledPlan.monthlyPrice : this.scheduledPlan.yearlyPrice)
      }
      return 0
    },
    selectedSubscriptionType: function () {
      return this.scheduledPlan ? this.scheduledSubscriptionType : this.subscriptionType
    },
    planPricePerUser: function () {
      return this.scheduledPlan
        ? getPlanPrice(this.selectedPlan, this.selectedSubscriptionType)
        : getCurrentPricePerUser(this.activeSubscription, this.selectedPlan, this.subscriptionType)
    }
  },
  data () {
    return {
      loadingRenew: false,
      loadingReactivate: false,
      infoMessageDialogData: null,
      cancelSubscriptionDialogIsOpen: false,
      startOwnSubscriptionDialogIsOpen: false
    }
  },
  mounted () {
    this.checkAndOpenChangePlanDialog()
    if (this.subscriptionAccessType === 'LIMITED' && !this.isEnterpriseUser) {
      store.dispatch('payments/getPaymentMethod')
    }
  },
  watch: {
    '$route.query.openChangePlan' (value) {
      if (value === 'true') {
        this.checkAndOpenChangePlanDialog()
      }
    }
  },
  methods: {
    checkAndOpenChangePlanDialog () {
      if (this.$route.query.openChangePlan === 'true') {
        this.$nextTick(() => {
          if (this.$refs.changePlanDialog) {
            this.$refs.changePlanDialog.openDialog()
            this.$router.replace({ query: {} })
          }
        })
      }
    },
    onCancelSubscriptionDialogClose () {
      this.cancelSubscriptionDialogIsOpen = false
      this.$refs.subscriptionCanceledDialog.openDialog()
    },
    startOwnSubscription (planValue) {
      this.startOwnSubscriptionDialogIsOpen = false
      store.commit('payments/removeAllUsers')
      const chosenPlan = PLANS.find((plan) => plan.value === planValue) || PLANS.find((plan) => plan.value === 'pro')
      store.commit('payments/setPlan', chosenPlan)

      this.$router.push('/checkout')
    },
    async onRecovery () {
      if (this.recoveryButton.action === 'billing') {
        this.$router.push('/subscription/billing/manage-billing')
        return
      }

      return this.onRenew()
    },
    async onRenew () {
      this.loadingRenew = true
      const status = this.activeSubscription?.lastSubscriptionDetails?.status

      if (status !== 'past_due') {
        const paymentMethod = await store.dispatch('payments/getPaymentMethod')
        if (!paymentMethod?.card?.last4) {
          this.loadingRenew = false
          this.$router.push('/plans')
          return
        }
      }

      const response = await store.dispatch('payments/renewSubscription')
      this.loadingRenew = false

      if (!response?.success) {
        this.infoMessageDialogData = {
          message: response?.message,
          isOpen: true
        }
      }
    },
    async onReactivate () {
      this.loadingReactivate = true
      const response = await store.dispatch('payments/reactivateSubscription')
      this.loadingReactivate = false

      if (!response?.success) {
        this.infoMessageDialogData = {
          message: response?.message || 'Could not reactivate the subscription. Please try again.',
          isOpen: true
        }
      }
    }
  }
}
</script>

<style scoped>
.page-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.page-container-body {
  flex-grow: 1;
  overflow: auto;
}

.page-loading-container-body {
  gap: 16px;
  height: 100%;
  display: flex;
  font-size: 16px;
  font-weight: 400;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  color: var(--dark-4, #363A40);
}

.page-container-footer {
  gap: 8px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-title {
  margin-bottom: 32px;
  font-size: 20px;
  font-weight: 700;
  color: var(--dark-4, #363A40);
}

.plan-badge {
  gap: 8px;
  padding: 8px;
  display: flex;
  font-size: 14px;
  font-weight: 400;
  border-radius: 8px;
  align-items: center;
  color: var(--dark-4, #363A40);
  background: var(--light-2, #F5F2F0);
}

.plan-info {
  padding: 24px;
}

.plan-info-header {
  gap: 24px;
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  justify-content: space-between;
}

.planName {
  font-size: 20px;
  font-weight: 400;
  color: var(--dark-4, #363A40);
}

.planPrice {
  font-size: 14px;
  font-weight: 500;
  color: var(--dark-2, #878F99);
}

.features {
  gap: 8px;
  margin: 0;
  padding: 0;
  display: flex;
  margin-bottom: 8px;
  flex-direction: column;
  list-style-type: none;
}

.featureItem {
  gap: 8px;
  display: flex;
}

.featureItemLabel {
  font-size: 14px;
  font-weight: 400;
  color: var(--dark-4, #363A40);
}

.viewAllPlans {
  font-size: 12px;
  font-weight: 400;
  color: var(--blue-3);
  padding-left: 32.5px;
  text-decoration: none;
}
</style>
